import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
    NetworkSection,
    NetworkSectionFilter,
    NetworkSectionId,
    NetworkSectionPagination,
    NetworkSectionSort,
} from '../domain/Network';

export const moduleName = 'NETWORK_STATE';

type State = {
    resetNetworkSectionsOngoing: boolean;
    networkSections: Array<NetworkSection>;
    selectedNetworkSectionIds: Array<NetworkSectionId>;
    sort: NetworkSectionSort;
    filter: NetworkSectionFilter;
    pagination: NetworkSectionPagination;
    columnVisibility: Record<string, boolean>;
};

const initialState: State = {
    resetNetworkSectionsOngoing: false,
    networkSections: [],
    selectedNetworkSectionIds: [],
    sort: [],
    filter: { items: [] },
    pagination: { page: 0, pageSize: 100 },
    columnVisibility: {
        name: false,
        prd: false,
        abd: false,
        prf: false,
        abf: false,
        gestionary: false,
    },
};

const networkStateSlice = createSlice({
    name: moduleName,
    initialState,
    reducers: {
        resetStarted(state: State) {
            state.resetNetworkSectionsOngoing = true;
            state.networkSections = [];
            state.selectedNetworkSectionIds = [];
        },
        resetWith(
            state: State,
            action: PayloadAction<{
                networkSections: Array<NetworkSection>;
                sort: NetworkSectionSort;
                filter: NetworkSectionFilter;
            }>,
        ) {
            state.sort = action.payload.sort;
            state.filter = action.payload.filter;
            state.networkSections = action.payload.networkSections;
            state.resetNetworkSectionsOngoing = false;
            state.selectedNetworkSectionIds = [];
        },
        setNetwork(state: State, action: PayloadAction<Array<NetworkSection>>) {
            state.networkSections = action.payload;
        },
        selectNetworkSection(state: State, action: PayloadAction<NetworkSectionId>) {
            if (!state.selectedNetworkSectionIds.includes(action.payload)) {
                state.selectedNetworkSectionIds.push(action.payload);
            }
        },
        updatedNetworkSection(state: State, action: PayloadAction<NetworkSection>) {
            const idx = state.networkSections.findIndex((aNetworkSection) => aNetworkSection.id === action.payload.id);
            if (idx < 0) {
                throw new Error(`Network Section ${action.payload.id} not found in store`);
            }
            state.networkSections[idx] = action.payload;
        },
        updatedNetworkSections(state: State, action: PayloadAction<Array<NetworkSection>>) {
            const incomingById = new Map(action.payload.map((ns) => [ns.id, ns]));
            state.networkSections = state.networkSections.map((existing) => incomingById.get(existing.id) ?? existing);
        },
        deselectNetworkSection(state: State, action: PayloadAction<NetworkSectionId>) {
            state.selectedNetworkSectionIds = state.selectedNetworkSectionIds.filter((id) => id !== action.payload);
        },
        paginationUpdated(state: State, action: PayloadAction<NetworkSectionPagination>) {
            state.pagination = { ...state.pagination, ...action.payload };
        },
        setSelectedNetworkSections(state: State, action: PayloadAction<Array<NetworkSectionId>>) {
            state.selectedNetworkSectionIds = action.payload;
        },
        setColumnVisibility(state: State, action: PayloadAction<Record<string, boolean>>) {
            state.columnVisibility = action.payload;
        },
        filterUpdated(state: State, action: PayloadAction<NetworkSectionFilter>) {
            state.filter = action.payload;
        },
    },
});

export const {
    resetStarted,
    resetWith,
    setNetwork,
    selectNetworkSection,
    deselectNetworkSection,
    setSelectedNetworkSections,
    paginationUpdated,
    updatedNetworkSection,
    updatedNetworkSections,
    setColumnVisibility,
    filterUpdated,
} = networkStateSlice.actions;
export const networkStateReducer = networkStateSlice.reducer;
